import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { IS_MOBILE_OR_TABLET_BROWSER, IS_WEBVIEW, IS_WEBVIEW_FROM_UA } from 'reactApp/appHelpers/configHelpers';
import { TRIAL_ONBOARDING_MODAL_ID, trialOnboardingModalConfig } from 'reactApp/appHelpers/featuresHelpers/features/trialOnboarding';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/TrialOnboardingModal/TrialOnboardingModal'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const trialOnboardingModalCreator: ModelCreator = ({ models, render, stores }) => {
    return new PopupModel(
        TRIAL_ONBOARDING_MODAL_ID,
        models.base,
        new BaseModelProps(TRIAL_ONBOARDING_MODAL_ID, {
            store: new LocalStorageStore(TRIAL_ONBOARDING_MODAL_ID, stores.localStorage),
            renderer: new ReactRenderer(TRIAL_ONBOARDING_MODAL_ID, SuspendedComponent, render.react),
            closable: true,
            showLimit: trialOnboardingModalConfig.showLimit,
            closeLimit: trialOnboardingModalConfig.closeLimit,
            showInterval: trialOnboardingModalConfig.showInterval,
            templateData: {},
            ignoreTestOnRedraw: true,
            markAsCompleted: true,
            hideOnCompleted: true,
            hideOnSnooze: true,
            test: (resolve, reject) => {
                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                if (IS_MOBILE_OR_TABLET_BROWSER || IS_WEBVIEW || IS_WEBVIEW_FROM_UA) {
                    reject();
                    return;
                }

                if (storage !== EStorageType.home) {
                    reject();
                    return;
                }

                const isViewer = ViewerSelectors.isViewerActive(state) || ViewerSelectors.isViewerOpening(state);

                if (isViewer) {
                    reject();
                    return;
                }

                const isCorpUser = UserSelectors.isCorpUser(state);
                const isBizUser = UserSelectors.isBizUser(state);

                if (isCorpUser || isBizUser) {
                    reject();
                    return;
                }

                const trialSubscription = BillingDataSelectors.getTrialSubscription(state);

                const isActiveTrial = trialSubscription && trialSubscription.expires > Date.now();

                const productId = trialSubscription?.productId || '';
                const isMinimalTariff = productId.toLowerCase().includes('minimal');

                const isReject = !isActiveTrial || isMinimalTariff;

                sendXray(['promo-test', TRIAL_ONBOARDING_MODAL_ID, isReject ? 'reject' : 'resolve']);

                if (isReject) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
};
